// Import mock API for development
import { mockBlindStructuresApi, shouldUseMockApi } from './mockBlindStructures';

// API Configuration and Service for Admin
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/poker-api';

// Helper for making API requests
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);

    // Try to parse JSON response
    let data;
    try {
      data = await response.json();
    } catch (e) {
      // If response is not JSON, create error message
      data = { message: `Server error: ${response.statusText || 'Unknown error'}` };
    }

    if (!response.ok) {
      // Create more descriptive error messages
      let errorMessage = data.message || data.error || `HTTP ${response.status}: ${response.statusText}`;

      // Add context for common status codes
      if (response.status === 500) {
        errorMessage = `Internal server error: ${errorMessage}`;
      } else if (response.status === 404) {
        errorMessage = `Not found: ${errorMessage}`;
      } else if (response.status === 403) {
        errorMessage = `Access denied: ${errorMessage}`;
      } else if (response.status === 400) {
        errorMessage = `Bad request: ${errorMessage}`;
      }

      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error(`API request failed: ${endpoint}`, error);
    throw error;
  }
}

// Users API
export const usersApi = {
  getUserByUsername: async (username) => {
    return apiRequest(`/users?username=${encodeURIComponent(username)}`);
  },

  loginAdmin: async (username, password) => {
    return apiRequest('/users/login-admin', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },

  getAll: async () => {
    return apiRequest('/users/all');
  },

  getDealers: async () => {
    return apiRequest('/users/dealers');
  },

  getUserByPhone: async (phone) => {
    return apiRequest(`/users/by-phone?phone=${encodeURIComponent(phone)}`);
  },

  create: async (userData) => {
    return apiRequest('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  update: async (userId, userData) => {
    return apiRequest(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  },

  verifyVipPassword: async (userId, password) => {
    return apiRequest('/users/verify-vip-password', {
      method: 'POST',
      body: JSON.stringify({ userId, password }),
    });
  },

  getStats: async (userId) => apiRequest(`/users/${userId}/stats`),

  updateRoles: async (userId, roles) => {
    return apiRequest(`/users/${userId}/roles`, {
      method: 'PUT',
      body: JSON.stringify({ roles }),
    });
  },
  archive: async (userIds) => {
    return apiRequest('/users/archive', {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    });
  },
  unarchive: async (userIds) => {
    return apiRequest('/users/unarchive', {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    });
  },
  getAllIncludingArchived: async () => {
    return apiRequest('/users/all?includeArchived=true');
  },
};

// Roles API
export const rolesApi = {
  getAll: async () => {
    return apiRequest('/roles');
  },
};

// Venues API
export const venuesApi = {
  getAll: async (leagueId = null) => {
    let endpoint = '/venues';
    if (leagueId) {
      endpoint += `?leagueId=${leagueId}`;
    }
    return apiRequest(endpoint);
  },

  create: async (venueData) => {
    return apiRequest('/venues', {
      method: 'POST',
      body: JSON.stringify(venueData),
    });
  },

  update: async (venueId, venueData) => {
    return apiRequest(`/venues/${venueId}`, {
      method: 'PUT',
      body: JSON.stringify(venueData),
    });
  },

  delete: async (venueId) => {
    return apiRequest(`/venues/${venueId}`, {
      method: 'DELETE',
    });
  },

  // Geocode an address to get lat/long
  geocodeAddress: async (address) => {
    // Use OpenStreetMap Nominatim for free geocoding
    const encodedAddress = encodeURIComponent(address);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`,
      {
        headers: {
          'User-Agent': 'PokerLeagueAdmin/1.0',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Geocoding service unavailable');
    }

    const data = await response.json();

    if (data.length === 0) {
      throw new Error('Address not found');
    }

    return {
      latitude: parseFloat(data[0].lat),
      longitude: parseFloat(data[0].lon),
      displayName: data[0].display_name,
    };
  },
};

// Keep locationsApi as alias for backward compatibility
export const locationsApi = venuesApi;

// Games API
export const gamesApi = {
  getAll: async (leagueId = null) => {
    let endpoint = '/games';
    if (leagueId) {
      endpoint += `?leagueId=${leagueId}`;
    }
    return apiRequest(endpoint);
  },

  getById: async (gameId) => {
    return apiRequest(`/games/${gameId}`);
  },

  getSeating: async (gameId) => {
    return apiRequest(`/games/${gameId}/seating`);
  },

  create: async (gameData) => {
    return apiRequest('/games', {
      method: 'POST',
      body: JSON.stringify(gameData),
    });
  },

  update: async (gameId, gameData) => {
    return apiRequest(`/games/${gameId}`, {
      method: 'PUT',
      body: JSON.stringify(gameData),
    });
  },

  delete: async (gameId) => {
    return apiRequest(`/games/${gameId}`, {
      method: 'DELETE',
    });
  },

  assignDealer: async (gameId, dealerId) => {
    return apiRequest(`/games/${gameId}/dealer`, {
      method: 'POST',
      body: JSON.stringify({ dealerId }),
    });
  },

  removeDealer: async (gameId, dealerId) => {
    return apiRequest(`/games/${gameId}/dealer`, {
      method: 'DELETE',
      body: JSON.stringify({ dealerId }),
    });
  },

  addTable: async (gameId, maxPlayers) => {
    return apiRequest(`/games/${gameId}/tables`, {
      method: 'POST',
      body: JSON.stringify({ maxPlayers }),
    });
  },

  removeTable: async (gameId, tableNumber) => {
    return apiRequest(`/games/${gameId}/tables/${tableNumber}`, {
      method: 'DELETE',
    });
  },

  assignDealerToTable: async (gameId, dealerId, tableNumber, options = {}) => {
    return apiRequest(`/games/${gameId}/dealers`, {
      method: 'POST',
      body: JSON.stringify({
        dealerId,
        tableNumber,
        recurring: options.recurring || false,
        replaceTable: options.replaceTable || false,
      }),
    });
  },

  removeDealerFromGame: async (gameId, dealerId) => {
    return apiRequest(`/games/${gameId}/dealers/${dealerId}`, {
      method: 'DELETE',
    });
  },

  getResults: async (gameId) => {
    return apiRequest(`/games/${gameId}/results`);
  },

  getReports: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.venueId) params.append('venueId', filters.venueId);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    return apiRequest(`/games/reports?${params.toString()}`);
  },

  reserveSeat: async (gameId, userId, userLat, userLon, tableId = null, seatNumber = null) => {
    return apiRequest(`/games/${gameId}/seats`, {
      method: 'POST',
      body: JSON.stringify({ userId, userLat, userLon, tableId, seatNumber }),
    });
  },

  swapSeat: async (gameId, userId, password, targetTableId, targetSeatNumber) => {
    return apiRequest(`/games/${gameId}/seats/swap`, {
      method: 'POST',
      body: JSON.stringify({ userId, password, targetTableId, targetSeatNumber }),
    });
  },

  dealerMoveSeat: async (gameId, userId, targetTableId, targetSeatNumber) => {
    return apiRequest(`/games/${gameId}/seats/dealer-swap`, {
      method: 'POST',
      body: JSON.stringify({ userId, targetTableId, targetSeatNumber }),
    });
  },

  joinWaitingList: async (gameId, userId, userLat, userLon) => {
    return apiRequest(`/games/${gameId}/waiting-list`, {
      method: 'POST',
      body: JSON.stringify({ userId, userLat, userLon }),
    });
  },

  getWaitingList: async (gameId) => {
    return apiRequest(`/games/${gameId}/waiting-list`);
  },
};

// Leagues API
export const leaguesApi = {
  getAll: async () => {
    return apiRequest('/leagues');
  },

  getById: async (leagueId) => {
    return apiRequest(`/leagues/${leagueId}`);
  },

  create: async (leagueData) => {
    return apiRequest('/leagues', {
      method: 'POST',
      body: JSON.stringify(leagueData),
    });
  },

  update: async (leagueId, leagueData) => {
    return apiRequest(`/leagues/${leagueId}`, {
      method: 'PUT',
      body: JSON.stringify(leagueData),
    });
  },

  delete: async (leagueId) => {
    return apiRequest(`/leagues/${leagueId}`, {
      method: 'DELETE',
    });
  },

  // League Points
  getPoints: async (leagueId) => {
    return apiRequest(`/leagues/${leagueId}/points`);
  },

  savePoints: async (leagueId, points) => {
    return apiRequest(`/leagues/${leagueId}/points`, {
      method: 'PUT',
      body: JSON.stringify({ points }),
    });
  },

  // Blind Structures for League
  getBlindStructures: async (leagueId) => {
    if (shouldUseMockApi()) {
      return mockBlindStructuresApi.getBlindStructures(leagueId);
    }
    return apiRequest(`/blind-structures?leagueId=${leagueId}`);
  },

  createBlindStructure: async (leagueId, structureData) => {
    if (shouldUseMockApi()) {
      return mockBlindStructuresApi.createBlindStructure(leagueId, structureData);
    }
    return apiRequest(`/blind-structures`, {
      method: 'POST',
      body: JSON.stringify({ ...structureData, leagueId }),
    });
  },

  updateBlindStructure: async (leagueId, structureId, structureData) => {
    if (shouldUseMockApi()) {
      return mockBlindStructuresApi.updateBlindStructure(leagueId, structureId, structureData);
    }
    return apiRequest(`/blind-structures/${structureId}`, {
      method: 'PUT',
      body: JSON.stringify(structureData),
    });
  },

  deleteBlindStructure: async (leagueId, structureId) => {
    if (shouldUseMockApi()) {
      return mockBlindStructuresApi.deleteBlindStructure(leagueId, structureId);
    }
    return apiRequest(`/blind-structures/${structureId}`, {
      method: 'DELETE',
    });
  },

  setDefaultBlindStructure: async (leagueId, structureId) => {
    if (shouldUseMockApi()) {
      return mockBlindStructuresApi.setDefaultBlindStructure(leagueId, structureId);
    }
    return apiRequest(`/blind-structures/${structureId}/set-default`, {
      method: 'POST',
    });
  },
};

// Blind Structures API
export const blindStructuresApi = {
  getById: async (structureId) => {
    if (shouldUseMockApi()) {
      // Not implemented in mock yet
      return { success: true, data: {} };
    }
    return apiRequest(`/blind-structures/${structureId}`);
  },

  getLevels: async (structureId) => {
    if (shouldUseMockApi()) {
      return mockBlindStructuresApi.getLevels(structureId);
    }
    return apiRequest(`/blind-structures/${structureId}/levels`);
  },

  createLevel: async (structureId, levelData) => {
    if (shouldUseMockApi()) {
      return mockBlindStructuresApi.createLevel(structureId, levelData);
    }
    return apiRequest(`/blind-structures/${structureId}/levels`, {
      method: 'POST',
      body: JSON.stringify(levelData),
    });
  },

  updateLevel: async (structureId, levelId, levelData) => {
    if (shouldUseMockApi()) {
      return mockBlindStructuresApi.updateLevel(structureId, levelId, levelData);
    }
    return apiRequest(`/blind-structures/${structureId}/levels/${levelId}`, {
      method: 'PUT',
      body: JSON.stringify(levelData),
    });
  },

  deleteLevel: async (structureId, levelId) => {
    if (shouldUseMockApi()) {
      return mockBlindStructuresApi.deleteLevel(structureId, levelId);
    }
    return apiRequest(`/blind-structures/${structureId}/levels/${levelId}`, {
      method: 'DELETE',
    });
  },

  reorderLevels: async (structureId, levelOrder) => {
    if (shouldUseMockApi()) {
      // Not implemented in mock yet
      return { success: true };
    }
    return apiRequest(`/blind-structures/${structureId}/levels/reorder`, {
      method: 'POST',
      body: JSON.stringify({ levelOrder }),
    });
  },
};

// Schedules API
export const schedulesApi = {
  getAll: async (leagueId) => {
    const params = leagueId ? `?leagueId=${leagueId}` : '';
    return apiRequest(`/schedules${params}`);
  },

  getById: async (scheduleId) => {
    return apiRequest(`/schedules/${scheduleId}`);
  },

  create: async (scheduleData) => {
    return apiRequest('/schedules', {
      method: 'POST',
      body: JSON.stringify(scheduleData),
    });
  },

  update: async (scheduleId, scheduleData) => {
    return apiRequest(`/schedules/${scheduleId}`, {
      method: 'PUT',
      body: JSON.stringify(scheduleData),
    });
  },

  delete: async (scheduleId, deleteFutureGames = false) => {
    const params = deleteFutureGames ? '?deleteFutureGames=true' : '';
    return apiRequest(`/schedules/${scheduleId}${params}`, {
      method: 'DELETE',
    });
  },

  generateGames: async (scheduleId, weeksAhead = 8) => {
    return apiRequest(`/schedules/${scheduleId}/generate`, {
      method: 'POST',
      body: JSON.stringify({ weeksAhead }),
    });
  },

  toggleActive: async (scheduleId, isActive) => {
    return apiRequest(`/schedules/${scheduleId}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive }),
    });
  },

  getDealers: async (scheduleId) => {
    return apiRequest(`/schedules/${scheduleId}/dealers`);
  },

  setDealer: async (scheduleId, tableNumber, dealerId) => {
    return apiRequest(`/schedules/${scheduleId}/dealers`, {
      method: 'POST',
      body: JSON.stringify({ tableNumber, dealerId }),
    });
  },

  removeDealer: async (scheduleId, tableNumber) => {
    return apiRequest(`/schedules/${scheduleId}/dealers/${tableNumber}`, {
      method: 'DELETE',
    });
  },
};

export const qualifiersApi = {
  getAll: async (type) => apiRequest(`/qualifiers${type ? '?type=' + type : ''}`),
  getCurrent: async (type = 'weekly') => apiRequest(`/qualifiers/current/entries?type=${type}`),
  getById: async (id) => apiRequest(`/qualifiers/${id}`),
  recordResults: async (id, results) => apiRequest(`/qualifiers/${id}/results`, {
    method: 'POST', body: JSON.stringify({ results })
  }),
  syncToPoker3d: async (type = 'weekly') => apiRequest('/qualifiers/sync-to-poker3d', {
    method: 'POST', body: JSON.stringify({ type })
  }),
};

export const championshipApi = {
  getSeats: async (year) => apiRequest(`/championship/seats${year ? '?year=' + year : ''}`),
  awardSeat: async (playerId, source, sourceId) => apiRequest('/championship/seats', {
    method: 'POST', body: JSON.stringify({ playerId, source, sourceId })
  }),
  redeemSeat: async (id) => apiRequest(`/championship/seats/${id}/redeem`, { method: 'POST' }),
  getAttendanceStats: async (leagueId) => apiRequest(`/leagues/${leagueId}/attendance-stats`),
  awardAttendanceSeats: async (leagueId) => apiRequest(`/leagues/${leagueId}/award-attendance-seats`, {
    method: 'POST', body: '{}'
  }),
};

export const userAdminApi = {
  getProfile: (userId) => apiRequest(`/users/${userId}/profile`),
  getStats: (userId) => apiRequest(`/users/${userId}/stats-detail`),
  getHistory: (userId) => apiRequest(`/users/${userId}/history`),
  getVenueBreakdown: (userId) => apiRequest(`/users/${userId}/venue-breakdown`),
  getPointsTimeline: (userId) => apiRequest(`/users/${userId}/points-timeline`),
  getAudit: (userId) => apiRequest(`/users/${userId}/audit`),
  getLoginHistory: (userId) => apiRequest(`/users/${userId}/login-history`),
  addTag: (userId, tag, color) => apiRequest(`/users/${userId}/tags`, { method: 'POST', body: JSON.stringify({ tag, color }) }),
  removeTag: (userId, tagId) => apiRequest(`/users/${userId}/tags/${tagId}`, { method: 'DELETE' }),
  updateMeta: (userId, payload) => apiRequest(`/users/${userId}/meta`, { method: 'PUT', body: JSON.stringify(payload) }),
  bulkRole: (userIds, roleName, actorId) => apiRequest('/users/bulk-role', { method: 'POST', body: JSON.stringify({ userIds, roleName, actorId }) }),
  bulkArchive: (userIds, actorId) => apiRequest('/users/bulk-archive', { method: 'POST', body: JSON.stringify({ userIds, actorId }) }),
  bulkVip: (userIds, expiration, actorId) => apiRequest('/users/bulk-vip', { method: 'POST', body: JSON.stringify({ userIds, expiration, actorId }) }),
  bulkResetPassword: (userIds, actorId) => apiRequest('/users/bulk-reset-password', { method: 'POST', body: JSON.stringify({ userIds, actorId }) }),
  getAnalytics: () => apiRequest('/admin/user-analytics'),
  getSuggestions: () => apiRequest('/admin/user-suggestions'),
  exportUser: (userId) => apiRequest(`/users/${userId}/export`),
  csvImport: (rows, actorId) => apiRequest('/users/csv-import', { method: 'POST', body: JSON.stringify({ rows, actorId }) }),
  uploadPhoto: (userId, dataUrl) => apiRequest(`/users/${userId}/photo`, { method: 'POST', body: JSON.stringify({ dataUrl }) }),
  mergePair: (fromUserId, toUserId, actorId) => apiRequest('/users/merge-pair', { method: 'POST', body: JSON.stringify({ fromUserId, toUserId, actorId }) }),
  forceLogout: (userId) => apiRequest(`/users/${userId}/force-logout`, { method: 'POST' }),
  setCustomField: (userId, key, value) => apiRequest(`/users/${userId}/custom-field`, { method: 'PUT', body: JSON.stringify({ key, value }) }),
};

export const dealerSchedulerApi = {
  bulkAssign: (gameIds, dealerId, tableNumber, actorId) => apiRequest('/scheduler/bulk-assign', { method: 'POST', body: JSON.stringify({ gameIds, dealerId, tableNumber, actorId }) }),
  bulkClear: (gameIds, actorId) => apiRequest('/scheduler/bulk-clear', { method: 'POST', body: JSON.stringify({ gameIds, actorId }) }),
  copyWeek: (sourceWeekStart, targetWeekStart, actorId) => apiRequest('/scheduler/copy-week', { method: 'POST', body: JSON.stringify({ sourceWeekStart, targetWeekStart, actorId }) }),
  autoFillWeek: (weekStart, actorId) => apiRequest('/scheduler/auto-fill-week', { method: 'POST', body: JSON.stringify({ weekStart, actorId }) }),
  checkConflicts: (dealerId, gameId) => apiRequest('/scheduler/check-conflicts', { method: 'POST', body: JSON.stringify({ dealerId, gameId }) }),
  getAuditLog: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest('/scheduler/audit-log' + (q ? '?' + q : ''));
  },
  listTemplates: () => apiRequest('/scheduler/templates'),
  saveTemplate: (name, description, payload, createdBy) => apiRequest('/scheduler/templates', { method: 'POST', body: JSON.stringify({ name, description, payload, createdBy }) }),
  applyTemplate: (templateId, weekStart, actorId) => apiRequest('/scheduler/templates/apply', { method: 'POST', body: JSON.stringify({ templateId, weekStart, actorId }) }),
  getComments: (gameId) => apiRequest(`/games/${gameId}/comments`),
  addComment: (gameId, authorId, body) => apiRequest(`/games/${gameId}/comments`, { method: 'POST', body: JSON.stringify({ authorId, body }) }),
  getDealerLoad: (weekStart) => apiRequest(`/scheduler/dealer-load?weekStart=${encodeURIComponent(weekStart)}`),
  getWeekShortages: (weekStart) => apiRequest(`/scheduler/week-shortages?weekStart=${encodeURIComponent(weekStart)}`),
  notifyDealer: (dealerId, title, body) => apiRequest('/scheduler/notify', { method: 'POST', body: JSON.stringify({ dealerId, title, body }) }),
  broadcast: (dealerIds, title, body) => apiRequest('/scheduler/broadcast', { method: 'POST', body: JSON.stringify({ dealerIds, title, body }) }),
  updateDealerMeta: (dealerId, color, rate) => apiRequest(`/dealers/${dealerId}/meta`, { method: 'PUT', body: JSON.stringify({ color, rate }) }),
};

export const duplicatesApi = {
  find: async () => apiRequest('/users/duplicates'),
  merge: async (fromId, toId) => apiRequest('/users/merge', {
    method: 'POST',
    body: JSON.stringify({ fromId, toId }),
  }),
};

export const dashboardApi = {
  getStats: async () => apiRequest('/dashboard/stats'),
  getShortages: async () => apiRequest('/dashboard/shortages'),
  getActivity: async () => apiRequest('/dashboard/activity'),
  getLateCheckins: async () => apiRequest('/dashboard/late-checkins'),
  autoFillShortages: async () => apiRequest('/dashboard/auto-fill-shortages', { method: 'POST', body: '{}' }),
};

export const publicApi = {
  getSchedule: async () => apiRequest('/public/schedule'),
  getVenues: async () => apiRequest('/public/venues'),
};

export const leagueLeaderboardApi = {
  get: async (leagueId) => apiRequest(`/leagues/${leagueId}/leaderboard`),
};

export const notificationsApi = {
  sendSMS: async (data) => apiRequest('/notifications/sms', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

export const promotionsApi = {
  getAll: async (leagueId) => {
    const params = leagueId ? `?leagueId=${leagueId}` : '';
    return apiRequest(`/promotions${params}`);
  },

  getById: async (id) => apiRequest(`/promotions/${id}`),

  create: async (data) => apiRequest('/promotions', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: async (id, data) => apiRequest(`/promotions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: async (id) => apiRequest(`/promotions/${id}`, {
    method: 'DELETE',
  }),

  addGames: async (id, gameIds) => apiRequest(`/promotions/${id}/games`, {
    method: 'POST',
    body: JSON.stringify({ gameIds }),
  }),

  removeGame: async (id, gameId) => apiRequest(`/promotions/${id}/games/${gameId}`, {
    method: 'DELETE',
  }),

  getLeaderboard: async (id) => apiRequest(`/promotions/${id}/leaderboard`),
};

export default {
  users: usersApi,
  venues: venuesApi,
  locations: locationsApi, // alias for backward compatibility
  games: gamesApi,
  leagues: leaguesApi,
  blindStructures: blindStructuresApi,
  schedules: schedulesApi,
  promotions: promotionsApi,
};
